// WHAT: Chart component for displaying Withings health metrics with normal range indicators.
// WHY:  Provides visual representation of health metrics over time with color-coded status indicators based on normal ranges.
// DOMAIN: Health → Withings
// DEPENDENCIES: React, @/components/ui/Card, @/components/charts/primitives/LineChart, ../../services/normRangeService, ../../types
// RISKS:
//   - NOTE: Normal range calculation depends on NormRangeService; ensure service is properly configured.
//   - WARN: Data filtering and sorting may impact performance with large datasets.
//   - NOTE: Date formatting uses 'de-DE' locale; may not match user preferences.

import React from "react";

import { LineChart, type LineChartDataPoint } from "@/components/charts/primitives/LineChart";
import { Card } from "@/components/ui/Card";

import { NormRangeService } from "../../services/normRangeService";
import { type WithingsMeasurement } from "../../types";

/**
 * Helper function to get status label text
 *
 * @param status - Status value
 *
 * @returns Status label text
 *
 */
function getStatusLabel(status: "below_range" | "elevated" | "normal" | "unknown"): string {
  if (status === "normal") {
    return "✓ Normal";
  }
  if (status === "elevated") {
    return "↑ Elevated";
  }
  if (status === "below_range") {
    return "↓ Below Range";
  }
  return "? Unknown";
}

/**
 * Helper function to process and sort measurement data
 *
 * @param data - Array of measurements
 *
 * @param dataKey - Key to extract metric value
 *
 * @returns Sorted array of valid measurements
 *
 */
function processMeasurementData<T extends WithingsMeasurement>(data: T[], dataKey: keyof T): T[] {
  const validData = data
    .filter((m) => {
      const value = m[dataKey];
      // eslint-disable-next-line eqeqeq -- Reason: dataKey can be any key of WithingsMeasurement, which includes null | number fields. Using != null checks for both null and undefined.
      return value != null;
    })
    .map((m) => ({
      ...m,
      [dataKey]: Number(m[dataKey]),
    }));
  // eslint-disable-next-line unicorn/no-array-sort -- Reason: toSorted() is not available in current TypeScript version
  return [...validData].sort(
    (a, b) => new Date(a.measurement_date).getTime() - new Date(b.measurement_date).getTime(),
  );
}

/**
 * Helper function to build reference lines for chart
 *
 * @param normRange - Normal range definition
 *
 * @param normRange.max
 *
 * @param normRange.min
 *
 * @param normRange.unit
 *
 * @param normRange.description
 *
 * @returns Array of reference line configurations
 *
 */
function buildReferenceLines(normRange: {
  max: number;
  min: number;
  unit: string;
  description: string;
}) {
  return [
    // Min Referenzlinie
    {
      label: {
        fill: "#10B981",
        fontSize: 10,
        position: "insideTopRight" as const,
        value: `Min: ${String(normRange.min)}`,
      },
      stroke: "#10B981",
      strokeDasharray: "3 3",
      strokeWidth: 1,
      value: normRange.min,
    },
    // Max Referenzlinie
    {
      label: {
        fill: "#EF4444",
        fontSize: 10,
        position: "insideTopRight" as const,
        value: `Max: ${String(normRange.max)}`,
      },
      stroke: "#EF4444",
      strokeDasharray: "3 3",
      strokeWidth: 1,
      value: normRange.max,
    },
  ];
}

interface MetricChartProperties {
  readonly className?: string;
  readonly color: string;
  readonly data: WithingsMeasurement[];
  readonly dataKey: keyof WithingsMeasurement;
  readonly normRangeKey: keyof ReturnType<typeof NormRangeService.getCurrentNormRanges>;
  readonly title: string;
  readonly unit: string;
}

/**
 * WHAT:
 *   Chart component for displaying Withings health metrics with normal range indicators.
 *
 * WHY:
 *   Provides users with visual representation of health metrics over time, including
 *   current value, status indicator (normal/elevated/below range), and trend visualization
 *   with normal range context.
 *
 * PROPS:
 *   - title: string - Chart title
 *   - data: WithingsMeasurement[] - Array of measurement data points
 *   - dataKey: keyof WithingsMeasurement - Key to extract metric value from measurements
 *   - unit: string - Unit of measurement (e.g., 'kg', 'cm')
 *   - color: string - Color for chart line and indicators
 *   - normRangeKey: keyof ReturnType<typeof NormRangeService.getCurrentNormRanges> - Key for normal range lookup
 *   - className?: string - Optional additional CSS classes
 *
 * BEHAVIOR:
 *   - Filters out null/undefined values from data
 *   - Sorts data by measurement date (ascending)
 *   - Calculates current value (last measurement)
 *   - Determines status (normal/elevated/below_range/unknown) based on normal ranges
 *   - Displays current value with status badge
 *   - Renders LineChart with formatted dates and values
 *   - Shows empty state when no valid data is available
 *
 * RISKS:
 *   - NOTE: Normal range calculation depends on NormRangeService; ensure service is properly configured.
 *   - WARN: Data filtering and sorting may impact performance with large datasets.
 *   - NOTE: Date formatting uses 'de-DE' locale; may not match user preferences.
 *   - WARN: Assumes data[0] is the most recent measurement; ensure data is sorted correctly.
 *
 * @param root0 - Component props
 *
 * @param root0.title - Chart title
 *
 * @param root0.data - Array of measurement data points
 *
 * @param root0.dataKey - Key to extract metric value from measurements
 *
 * @param root0.unit - Unit of measurement (e.g., 'kg', 'cm')
 *
 * @param root0.color - Color for chart line and indicators
 *
 * @param root0.normRangeKey - Key for normal range lookup
 *
 * @param root0.className - Optional additional CSS classes
 *
 * @returns JSX element displaying metric chart with normal range indicators
 *
 */
export const MetricChart: React.FC<MetricChartProperties> = ({
  className,
  color,
  data,
  dataKey,
  normRangeKey,
  title,
  unit,
}) => {
  // NOTE: Get current normal ranges from service
  const normRanges = NormRangeService.getCurrentNormRanges();
  const normRange = normRanges[normRangeKey];

  // NOTE: Process and sort data
  const sortedData = processMeasurementData(data, dataKey);

  // NOTE: Current value is the last measurement (first item in array after sorting)
  const currentValue = sortedData[0]?.[dataKey] as number;
  const currentStatus = currentValue
    ? NormRangeService.getValueStatus(currentValue, normRangeKey)
    : "unknown";

  if (sortedData.length === 0) {
    return (
      <Card className={`bg-slate-800/50 border-slate-700 text-white ${className ?? ""}`}>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
          <div className="text-center text-slate-400 py-8">No data available</div>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`bg-slate-800/50 border-slate-700 text-white ${className ?? ""}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {currentValue ? (
            <div className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-white">
                {currentValue.toFixed(1)} {unit}
              </span>
              <div
                className={`px-2 py-1 rounded-full text-xs font-medium ${NormRangeService.getStatusBgColor(
                  currentStatus,
                )} ${NormRangeService.getStatusColor(currentStatus)}`}
              >
                {getStatusLabel(currentStatus)}
              </div>
            </div>
          ) : null}
        </div>

        {/* Norm Range Info */}
        <div className="mb-4 p-3 bg-slate-700/50 rounded-lg">
          <div className="text-sm text-slate-300 mb-1">Normal Range:</div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-400">
              {normRange.min} - {normRange.max} {normRange.unit}
            </span>
            <span className="text-xs text-slate-500">{normRange.description}</span>
          </div>
        </div>

        {/* Chart */}
        <LineChart
          className="w-full"
          color={color}
          data={sortedData as unknown as LineChartDataPoint[]}
          gridLines={{
            horizontal: true,
            horizontalColor: "rgba(55, 65, 81, 0.3)",
            vertical: false,
          }}
          height={300}
          referenceLines={{
            y: buildReferenceLines(normRange),
          }}
          smartAxis={{
            enabled: true,
            maxTicks: 8,
            minTicks: 5,
            normRange: { max: normRange.max, min: normRange.min },
            padding: 0.1, // 10% padding
            preferIntegerTicks: normRangeKey === "visceralFat", // Special handling for visceral fat
          }}
          xKey="measurement_date"
          yAxisTickFormatter={(value) => `${String(value)} ${unit}`}
          yKey={dataKey as string}
        />

        {/* Statistics */}
        {sortedData.length > 1 && (
          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-xs text-slate-400">Minimum</div>
              <div className="text-sm font-medium text-white">
                {Math.min(...sortedData.map((d) => d[dataKey] as number)).toFixed(1)} {unit}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400">Average</div>
              <div className="text-sm font-medium text-white">
                {(
                  sortedData.reduce((sum, d) => sum + (d[dataKey] as number), 0) / sortedData.length
                ).toFixed(1)}{" "}
                {unit}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400">Maximum</div>
              <div className="text-sm font-medium text-white">
                {Math.max(...sortedData.map((d) => d[dataKey] as number)).toFixed(1)} {unit}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
